<script>
import { VueDraggable } from 'vue-draggable-plus';
import Loader from "@/components/Loader.vue";
import IconUploader from "@/components/trainings/form/IconUploader.vue";
import DeleteWithConfirmation from "@/components/DeleteWithConfirmation.vue";
import {SUPPORTED_LOCALES} from "@/utils/locale.js";

export default {
    components: {
        DeleteWithConfirmation,
        IconUploader,
        Loader,
        VueDraggable,
    },
    props: ['training'],
    data() {
        return {
            errors: {},

            trainingSearch: '',
            searchResults: [],
            searchLoading: false,
            searchDebounceTimer: null,
            searchPage: 1,
            searchHasMore: false,
            iconImageFile: null,
            iconImagePreview: null,
            iconImageRemoved: false,

            selectedTrainings: [],
            loading: false,
        };
    },
    methods: {
        async submit() {
            this.loading = true;
            this.errors = {};
            try {
                const formData = new FormData();
                formData.append('name', this.training.name);
                formData.append('is_active', this.training.is_active);
                formData.append('price', this.training.price);
                formData.append('description', this.training.description);
                formData.append('is_series', '1');
                formData.append('language', this.training.language || '');
                this.selectedTrainings.forEach((t, i) => {
                    formData.append(`trainings[${i}][id]`, t.id);
                    formData.append(`trainings[${i}][order]`, i + 1);
                });
                if (this.iconImageFile) {
                    formData.append('icon_image', this.iconImageFile);
                } else if (this.iconImageRemoved) {
                    formData.append('icon_image_removed', '1');
                }


                let url = '/trainings';
                if (this.training.id) {
                    url += '/' + this.training.id;
                }

                axios.post(url, formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                }).then(() => {
                    this.$toast.success(this.$t('trainings.messages.training_saved'));
                    this.$router.push({ name: 'trainings.index' });
                }).catch((data) => {
                    this.errors = data.response?.data?.errors || {};
                }).finally(() => {
                    this.loading = false;
                })

            } catch (error) {
                this.loading = false;
                if (error.response?.status === 422) {
                    this.errors = error.response.data.errors;
                } else {
                    this.$toast.error(this.$t('messages.error_try_again'));
                }
            }
        },

        onSearchInput() {
            clearTimeout(this.searchDebounceTimer);

            this.searchDebounceTimer = setTimeout(() => {
                this.searchPage = 1;

                if (this.trainingSearch.length < 2) {
                    this.searchResults = [];
                    this.searchHasMore = false;
                    return;
                }
                this.fetchTrainings();
            }, 350);
        },

        async fetchTrainings(append = false) {
            this.searchLoading = true;
            try {
                const response = await axios.get('/trainings', {
                    params: {
                        page: this.searchPage,
                        filters: {
                            name: this.trainingSearch,
                            is_series: 0,
                            is_active: 1
                        },
                    },
                });

                const data = response.data.data;
                const meta = response.data.meta;

                if (append) {
                    this.searchResults.push(...data);
                } else {
                    this.searchResults = data;
                }

                this.searchHasMore = meta.current_page < meta.last_page;
            } finally {
                this.searchLoading = false;
            }
        },

        loadMoreResults() {
            this.searchPage++;
            this.fetchTrainings(true);
        },

        addTraining(training) {
            const alreadyAdded = this.selectedTrainings.some(t => t.id === training.id);
            if (!alreadyAdded) {
                this.selectedTrainings.push({ ...training });
            }
        },

        removeTraining(index) {
            this.selectedTrainings.splice(index, 1);
        },

        isSelected(training) {
            return this.selectedTrainings.some(t => t.id === training.id);
        },

        removeIconImage() {
            this.iconImageFile = null;
            this.iconImageRemoved = true;
            if (this.iconImagePreview) {
                URL.revokeObjectURL(this.iconImagePreview);
                this.iconImagePreview = null;
            }
            this.training.icon_image_url = null;
        },

        onIconImageChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.iconImageFile = file;
                this.iconImagePreview = URL.createObjectURL(file);
                this.iconImageRemoved = false;
            }
        },

        onDelete() {
            this.$router.back();
        },
    },

    computed: {
        languageOptions() {
            return SUPPORTED_LOCALES.map(code => ({
                value: code,
                label: this.$t(`languages.${code}`)
            }));
        }
    },

    mounted() {
        if (this.training?.trainings) {
            this.selectedTrainings = [...this.training.trainings];
        }
    },
};
</script>

<template>
    <div class="training-form">

        <form @submit.prevent="submit">

            <b-field :label="$t('trainings.fields.name.name')" horizontal custom-class="mb-3"
                     :type="errors.name ? 'is-danger' : null"
                     :message="errors.name ? errors.name[0] : null">
                <b-input required v-model="training.name" icon="school"></b-input>
            </b-field>

            <b-field :label="$t('trainings.fields.is_active.name')" label-for="is_active" class="checkbox-wrapper mb-4" horizontal>
                <b-checkbox v-model="training.is_active"
                            input-id="is_active"
                            true-value="1"
                            false-value="0">
                </b-checkbox>
            </b-field>

            <b-field :label="$t('trainings.fields.price.name')" horizontal custom-class="mb-3"
                     :type="errors.price ? 'is-danger' : null"
                     :message="errors.price ? errors.price[0] : null">
                <b-input required v-model="training.price" icon="cash"></b-input>
            </b-field>

            <IconUploader
                :errors="errors.icon_image"
                :iconImageFile="iconImageFile"
                :iconImagePreview="iconImagePreview || training.icon_image_url"
                @onIconImageChange="onIconImageChange"
                @onRemoveIconImage="removeIconImage"
            />

            <b-field :label="$t('trainings.fields.description.name')" horizontal custom-class="mb-3"
                     :type="errors.description ? 'is-danger' : null"
                     :message="errors.description ? errors.description[0] : null">
                <b-input required maxlength="65535" type="textarea" v-model="training.description"></b-input>
            </b-field>

            <b-field :label="$t('trainings.fields.language.name')" horizontal custom-class="mb-3"
                     :type="errors.language ? 'is-danger' : null"
                     :message="errors.language ? errors.language[0] : null">
                <b-select v-model="training.language" expanded>
                    <option v-for="opt in languageOptions" :key="opt.value" :value="opt.value">
                        {{ opt.label }}
                    </option>
                </b-select>
            </b-field>

            <b-field
                :label="$t('training_series.fields.trainings.name')"
                horizontal
                custom-class="mb-3"
            >
                <div class="trainings-picker">

                    <div class="trainings-picker__search">
                        <b-input
                            v-model="trainingSearch"
                            @input="onSearchInput"
                            icon="magnify"
                            :placeholder="$t('training_series.fields.trainings.placeholder')"
                        />

                        <div v-if="searchResults.length" class="search-results mt-2">

                            <div
                                v-for="training in searchResults"
                                :key="training.id"
                                class="search-result-item"
                                :class="{ 'is-selected': isSelected(training) }"
                                @click="addTraining(training)"
                            >
                                <span>{{ training.name }}</span>
                                <b-icon
                                    v-if="isSelected(training)"
                                    icon="check"
                                    size="is-small"
                                    type="is-success"
                                />
                                <b-icon v-else icon="plus" size="is-small" />
                            </div>

                            <div v-if="searchLoading" class="has-text-centered mt-2">
                                {{ $t('messages.searching') }}
                            </div>

                            <b-button
                                v-if="searchHasMore && !searchLoading"
                                size="is-small"
                                class="mt-2 ml-2 mb-2"
                                @click="loadMoreResults"
                            >
                                {{ $t('actions.load_more') }}
                            </b-button>
                        </div>
                    </div>

                    <div class="trainings-picker__selected">
                        <p class="label mb-2">
                            {{ $t('training_series.labels.selected_trainings') }}
                            ({{ selectedTrainings.length }})
                        </p>

                        <p v-if="selectedTrainings.length === 0" class="has-text-grey is-size-7">
                            {{ $t('training_series.labels.none_selected') }}
                        </p>

                        <VueDraggable
                            v-show="selectedTrainings.length"
                            v-model="selectedTrainings"
                            handle=".drag-handle"
                            animation="200"
                            class="selected-list"
                        >
                            <div
                                v-for="(training, index) in selectedTrainings"
                                :key="training.id"
                                class="selected-item"
                            >
                                <b-icon class="drag-handle" icon="drag-vertical" />
                                <span class="order-badge">{{ index + 1 }}</span>
                                <span class="training-name">{{ training.name }}</span>
                                <b-button
                                    icon-right="close"
                                    size="is-small"
                                    type="is-ghost has-text-danger"
                                    @click="removeTraining(index)"
                                />
                            </div>
                        </VueDraggable>
                    </div>

                </div>
            </b-field>


            <section>
                <div class="buttons">
                    <b-button type="is-primary" native-type="submit" :loading="loading">
                        {{ $t('actions.save') }}
                    </b-button>

                    <DeleteWithConfirmation v-if="training.id"
                                              v-on:deleted="onDelete"
                                              :content="$t('trainings.messages.delete_training_confirmation')"
                                              :button-title="$t('trainings.labels.delete_training')"
                                              :url="'/trainings/' + $route.params.id"/>

                    <b-button @click.prevent="$router.push({name: 'trainings.index'})">
                        {{ $t('actions.back') }}
                    </b-button>
                </div>
            </section>

        </form>
    </div>
</template>

<style scoped lang="scss">

@use "bulma/sass/utilities/mixins";

.trainings-picker {
    display: flex;
    gap: 1.5rem;
    align-items: flex-start;
    flex-direction: column;

    @include mixins.desktop {
        flex-direction: row;
    }
}

.trainings-picker__search {
    flex: 1;
    max-width: 360px;
}

.search-results {
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid var(--bulma-border);
    border-radius: 4px;
}

.search-result-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    transition: background 0.15s;
}

.search-result-item:hover {
    background: var(--bulma-background);
}

.search-result-item.is-selected {
    background: #f0faf0;
    color: #257942;
}

.trainings-picker__selected {
    flex: 1;
}

.selected-list {
    min-height: 60px;
    border: 1px solid var(--bulma-border);
    border-radius: 4px;
    padding: 0.5rem;
}

.selected-item {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.4rem 0.5rem;
    background: var(--bulma-background);
    border: 1px solid var(--bulma-border);
    border-radius: 4px;
    margin-bottom: 0.4rem;
}

.drag-handle {
    cursor: grab;
    color: #aaa;
}

.order-badge {
    font-size: 0.75rem;
    font-weight: 600;
    color: #888;
    min-width: 1.2rem;
    text-align: center;
}

.training-name {
    flex: 1;
    font-size: 0.9rem;
}
</style>
